import type { Panel } from '../panel.class';
import type { Countertop } from '../../countertops/countertop.class';
import type { BacksplashDimensions } from '../types';

/**
 * Options for calculating backsplash dimensions
 */
export type CalculateBacksplashDimensionsOptions = {
	/** Use stored left adjoining depth instead of looking up current state */
	storedLeftAdjoiningDepth?: number;
	/** Use stored right adjoining depth instead of looking up current state */
	storedRightAdjoiningDepth?: number;
};

/**

 * @param associatedPanelId The ID of the panel this backsplash is associated with
 * @param countertop The countertop interface providing width and offset calculations
 * @param options Optional stored adjoining depths for updates
 * @returns The calculated width, center offset, and adjoining depths for the backsplash
 */
export const calculateBacksplashDimensions = (
	associatedPanelId: string,
	countertop: Countertop,
	options?: CalculateBacksplashDimensionsOptions
): BacksplashDimensions | null => {
	const island = countertop.refMap.kitchen?.design.islands.findById(countertop.islandIds[0]);
	const associatedPanel = island?.panels.findById(associatedPanelId) as Panel | null;

	if (!associatedPanel) return null;

	// Get the actual countertop width for this panel's side
	let countertopWidth = countertop.getCountertopWidthForPanel(associatedPanelId);

	// Determine adjoining depths - use stored values if provided (update case),
	// otherwise look up current state (creation case)
	let leftAdjoiningDepth: number;
	let rightAdjoiningDepth: number;

	if (options?.storedLeftAdjoiningDepth !== undefined) {
		// Update case: use stored value
		leftAdjoiningDepth = options.storedLeftAdjoiningDepth;
	} else {
		const leftPanel = associatedPanel.getLeftPanel();
		const leftBacksplash = leftPanel ? countertop.getBacksplashForPanel(leftPanel.id) : null;
		leftAdjoiningDepth = (leftBacksplash && leftBacksplash.size.depth > 0) ? leftBacksplash.size.depth : 0;
	}

	if (options?.storedRightAdjoiningDepth !== undefined) {
		rightAdjoiningDepth = options.storedRightAdjoiningDepth;
	} else {
		const rightPanel = associatedPanel.getRightPanel();
		const rightBacksplash = rightPanel ? countertop.getBacksplashForPanel(rightPanel.id) : null;
		rightAdjoiningDepth = (rightBacksplash && rightBacksplash.size.depth > 0) ? rightBacksplash.size.depth : 0;
	}

	countertopWidth -= leftAdjoiningDepth;
	countertopWidth -= rightAdjoiningDepth;
	const countertopCenterOffset = countertop.getCountertopCenterOffsetForPanel(associatedPanelId);

	// Calculate adjoining backsplash offset to center the backsplash on the remaining area
	// When left side is cut, shift right by half the depth (positive offset)
	// When right side is cut, shift left by half the depth (negative offset)
	const adjoiningBacksplashOffset = (leftAdjoiningDepth - rightAdjoiningDepth) / 2;

	// Negate for back panel and left panel since they face opposite direction
	const panelAngle = associatedPanel.getOriginAngle() ?? 0;
	const normalizedAngle = ((panelAngle % 360) + 360) % 360;
	const isBackPanel = Math.abs(normalizedAngle - 180) < 1;
	const isLeftPanel = Math.abs(normalizedAngle - 270) < 1;
	const finalAdjoiningOffset = (isBackPanel || isLeftPanel) ? -adjoiningBacksplashOffset : adjoiningBacksplashOffset;

	// Total offset = countertop center shift + corner adjustment
	const totalCenterOffset = countertopCenterOffset + finalAdjoiningOffset;

	return {
		width: countertopWidth,
		centerOffset: totalCenterOffset,
		leftAdjoiningDepth,
		rightAdjoiningDepth
	};
}
